import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useToggleVisibilityMutation } from '@entities/Module';
import { useWordsByUnitQuery } from '@entities/Word';
import { buildCramPath, buildModulePreviewPath, paths } from '@shared/config';
import type { ModuleSummary } from '@shared/types';
import { ChevronLeftIcon, PlayIcon } from '@shared/ui';
import { ModuleMenu } from './ModuleMenu';
import { RestoreBanner } from './RestoreBanner';
import './ModuleDetail.css';

/** Иконка «стопка карточек» — вход в ознакомление. */
const CardsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="7" width="13" height="14" rx="2.5" />
    <path d="M8 3h10.5A2.5 2.5 0 0 1 21 5.5V16" />
  </svg>
);

interface ModuleHeroProps {
  module: ModuleSummary;
}

/**
 * Верх страницы модуля: «← все модули», видимость и меню, название и баннер
 * тренировки со входом в ознакомление. У удалённого модуля вместо баннера —
 * предложение восстановить, видимости и меню нет.
 */
export function ModuleHero({ module: m }: ModuleHeroProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const visibility = useToggleVisibilityMutation();
  const trainTitleId = useId();
  // Кол-во «готовых» карточек = что листать в ознакомлении. Запрос делит кеш со
  // списком слов (тот же ключ), повторной загрузки нет. Нет готовых — вход прячем.
  const { data: words } = useWordsByUnitQuery(m.id);
  const readyCount = (words ?? []).filter((w) => w.ru && !w.isNeedAction && !w.isHidden).length;

  return (
    <>
      <div className="md-top">
        <Link to={paths.words} className="md-back">
          <ChevronLeftIcon className="md-back-icon" aria-hidden="true" />
          <span className="md-back-text">
            <span aria-hidden="true">← </span>
            {t('moduleDetail.backToUnits')}
          </span>
        </Link>
        {!m.deleted && (
          <div className="md-controls">
            <button
              type="button"
              role="switch"
              aria-checked={m.isPublic}
              className="md-vis"
              title={t(m.isPublic ? 'moduleDetail.visibility.publicHint' : 'moduleDetail.visibility.privateHint')}
              disabled={visibility.isPending}
              onClick={() => visibility.mutate({ id: m.id, isPublic: !m.isPublic })}
            >
              {t(m.isPublic ? 'moduleDetail.visibility.public' : 'moduleDetail.visibility.private')}
              <span className="md-switch" aria-hidden="true" />
            </button>
            <ModuleMenu module={m} />
          </div>
        )}
      </div>

      <header className="md-head">
        <div className="md-kicker">{t('moduleDetail.kicker')}</div>
        <h1 className="md-title">{m.title}</h1>
        {m.desc && <p className="md-desc">{m.desc}</p>}
      </header>

      {m.deleted ? (
        <div className="md-restore">
          <RestoreBanner module={m} />
        </div>
      ) : (
        <section className="md-train" aria-labelledby={trainTitleId}>
          <div className="md-train-text">
            <div className="md-train-kicker">{t('moduleDetail.cramHero.kicker')}</div>
            <h2 id={trainTitleId} className="md-train-title">{t('moduleDetail.cramHero.title')}</h2>
            <p className="md-train-sub">{t('moduleDetail.cramHero.sub')}</p>
          </div>
          <div className="md-train-actions">
            {readyCount > 0 && (
              <button type="button" className="md-acq" onClick={() => navigate(buildModulePreviewPath(m.id))}>
                <CardsIcon />
                {t('moduleDetail.acquaint')}
              </button>
            )}
            <button
              type="button"
              className="md-start"
              title={t('moduleDetail.cramTooltip')}
              onClick={() => navigate(buildCramPath(m.id))}
            >
              <PlayIcon aria-hidden="true" />
              {t('moduleDetail.studyThis')}
            </button>
          </div>
        </section>
      )}
    </>
  );
}
